import { useBlocker, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Rnd } from 'react-rnd'

import {
  AttachFileIcon,
  CloseIcon,
  DeleteIcon,
  DocumentIcon,
  DragIndicator,
  EditIcon,
  MinimizeIcon,
  MoreVertIcon,
  WebAssetIcon,
} from '@/components/icon'
import {
  Avatar,
  Box,
  Button,
  Center,
  Checkbox,
  CounterInput,
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
  FieldRoot,
  FileUploadHiddenInput,
  FileUploadRootProvider,
  FileUploadTrigger,
  For,
  Grid,
  HStack,
  IconButton,
  IconButtonProps,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuTrigger,
  Portal,
  Separator,
  Stack,
  Tag,
  Text,
  Textarea,
  toaster,
  Tooltip,
  useFileUpload,
  UseFileUploadReturn,
} from '@/components/ui'
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys'
import {
  ApiError,
  generateGraphQLCacheKey,
  graphqlRequest,
  mutateCache,
  useGqlMutation,
  useGqlQuery,
  useMutation,
} from '@/lib/api'
import { useAuthenticatedUser, usePermission } from '@/lib/auth'
import { useBreakpointValue } from '@/lib/chakra-ui'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import {
  GetInquiriesDocument,
  GetInquiryDocument,
  InquiryLogAction,
  InquiryLogStatus,
  NewInquiryMessageInputAttachment,
  ResourcePermissionTargetInquiryRole,
} from '@/lib/gql/graphql'
import { assertNever } from '@/utils/assertNever'
import { formatDate } from '@/utils/datetime'
import { getFullName } from '@/utils/getFullName'

import { AttachmentsDialog } from './AttachmentsDialog'
import {
  CriteriaValue,
  CriteriaValueInputDialog,
} from './CriteriaValueInputDialog'
import { uploadFiles } from './uplaodFiles'
import { useInquiryDialogPosition } from './useInquiryDialogPosition'
import { useNewInquiryContext } from './useNewInquiry'
import {
  ACCEPTABLE_EXTENSIONS,
  validateFileExtension,
  validateInquiryMessage,
  validateMessageLength,
} from './validateInquiryMessage'

const CompleteInquiryDialog = ({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}) => {
  return (
    <DialogRoot
      open={open}
      onOpenChange={e => {
        onOpenChange(e.open)
      }}
    >
      <DialogContent>
        <DialogHeader fontSize="lg" fontWeight="bold" color="gray.700">
          問い合わせを完了する
        </DialogHeader>
        <DialogBody>
          <Text whiteSpace="pre-wrap">
            問い合わせを完了しますか？
            <br />
            完了した問い合わせは再開することが可能です。
          </Text>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="solid" colorPalette="gray">
              キャンセル
            </Button>
          </DialogActionTrigger>
          <DialogActionTrigger asChild>
            <Button onClick={onComplete}>問い合わせを完了する</Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

const CloseConfirmationDialog = ({
  open,
  isNew,
  onCancel,
  onProceed,
}: {
  open: boolean
  isNew: boolean
  onCancel: () => void
  onProceed: () => void
}) => {
  return (
    <DialogRoot open={open}>
      <DialogContent>
        <DialogHeader fontSize="lg" fontWeight="bold" color="gray.700">
          問い合わせを中止しますか？
        </DialogHeader>
        <DialogBody>
          <Text whiteSpace="pre-wrap">
            {isNew
              ? '中止する場合、入力中のメッセージ・選択除外基準は破棄されます。'
              : '中止する場合、入力中のメッセージは破棄されます。'}
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button variant="solid" colorPalette="gray" onClick={onCancel}>
            キャンセル
          </Button>
          <Button onClick={onProceed} colorPalette="red">
            破棄して閉じる
          </Button>
        </DialogFooter>
        <DialogCloseTrigger onClick={onCancel} />
      </DialogContent>
    </DialogRoot>
  )
}

const inquiryHeaderTrialFragment = graphql(`
  fragment InquiryHeaderTrial on Trial {
    trialUid
    networkUid
    ...CriteriaValueDialogTrial
  }
`)
const inquiryHeaderInquiryFragment = graphql(`
  fragment InquiryHeaderInquiry on Inquiry {
    ...AttachmentDialogInquiry
    ...CriteriaValueDialogInquiry
    inquiryUid
    inquiryNumber
    inquiryTitle
    resourcePermissions {
      holder {
        ... on ResourcePermissionHolderUser {
          userUid
        }
      }
      target {
        __typename
        ... on ResourcePermissionTargetInquiry {
          role
        }
      }
    }
    latestLog {
      status
    }
    firstLog {
      author {
        ... on BusinessUser {
          organizationName
          generalUser {
            firstName
            lastName
          }
        }
      }
    }
    latestCriteriaValues {
      criteriaFieldValues {
        fid
        value
      }
    }
  }
`)
const completeInquiryMutation = graphql(`
  mutation CompleteInquiry($input: CompleteInquiryInput!) {
    completeInquiry(input: $input) {
      inquiryUid
    }
  }
`)

const editInquiryTitleMutation = graphql(`
  mutation EditInquiryTitle($input: EditInquiryTitleInput!) {
    editInquiryTitle(input: $input) {
      inquiryUid
    }
  }
`)

const InquiryDialogHeader = ({
  trial,
  inquiry,
  isDragging,
  isMessageFormEmpty,
  onMinimize,
  newCriteriaValues,
  onChangeCriteriaValue,
  resetMessage,
  resetRejectedFiles,
}: {
  trial: FragmentType<typeof inquiryHeaderTrialFragment>
  inquiry?: FragmentType<typeof inquiryHeaderInquiryFragment>
  isDragging?: boolean
  isMessageFormEmpty?: boolean
  onMinimize: IconButtonProps['onClick']
  /** 新規作成時の編集中のCriteriaValues */
  newCriteriaValues?: CriteriaValue[]
  /** 選択除外基準を更新した際に呼ぶ処理 */
  onChangeCriteriaValue: ((values: CriteriaValue[]) => void) | null
  resetMessage: () => void
  resetRejectedFiles: () => void
}) => {
  const isDesktop = useBreakpointValue({ base: false, desktop: true })

  const user = useAuthenticatedUser()

  const inquiryData = getFragmentData(inquiryHeaderInquiryFragment, inquiry)
  const trialData = getFragmentData(inquiryHeaderTrialFragment, trial)

  const userName = getFullName({
    firstName: inquiryData
      ? inquiryData.firstLog.author.generalUser.firstName
      : user.generalUser.firstName,
    lastName: inquiryData
      ? inquiryData.firstLog.author.generalUser.lastName
      : user.generalUser.lastName,
  })
  const organizationName = inquiryData
    ? inquiryData.firstLog.author.organizationName
    : user.organizationName

  const navigate = useNavigate()

  const {
    title: newInquiryTitle,
    onChangeTitle: onChangeNewInquiryTitle,
    closeInquiryDialog: closeNewInquiryDialog,
  } = useNewInquiryContext()

  const inquiryTitle = inquiryData?.inquiryTitle ?? newInquiryTitle

  const { hasPermission } = usePermission()

  const [editingTitle, setEditingTitle] = useState(inquiryTitle)

  useEffect(() => {
    setEditingTitle(inquiryTitle)
  }, [inquiryTitle])

  const getInquiryCacheKey = generateGraphQLCacheKey(GetInquiryDocument, {
    inquiryUid: inquiryData?.inquiryUid ?? '',
  })
  const getInquiriesCacheKey = generateGraphQLCacheKey(GetInquiriesDocument, {
    trialUid: trialData.trialUid,
  })

  const { request: completeInquiry } = useGqlMutation(completeInquiryMutation, {
    onSuccess: () => {
      toaster.create({
        type: 'success',
        title: '問い合わせを完了しました',
      })
      navigate({
        to: '.',
        replace: true,
        search: {
          inquiry: undefined,
        },
      })
    },
    cacheKeysOnSuccess: [getInquiryCacheKey, getInquiriesCacheKey],
  })
  const { request: editInquiryTitle } = useGqlMutation(
    editInquiryTitleMutation,
    {
      onSuccess: () => {
        toaster.create({
          type: 'success',
          title: 'タイトルを変更しました',
        })
      },
      cacheKeysOnSuccess: [getInquiryCacheKey, getInquiriesCacheKey],
    },
  )

  const isInquirer =
    !inquiryData ||
    inquiryData.resourcePermissions.some(rp => {
      if (rp.target.__typename !== 'ResourcePermissionTargetInquiry')
        return false
      return (
        rp.holder.userUid === user.userUid &&
        rp.target.role === ResourcePermissionTargetInquiryRole.Inquirer
      )
    })
  const isCompleted =
    inquiryData?.latestLog.status === InquiryLogStatus.Completed

  const [completeDialogOpen, setCompleteDialogOpen] = useState(false)

  const defaultCriteriaValues: CriteriaValue[] =
    inquiryData?.latestCriteriaValues
      .flatMap(cv => cv.criteriaFieldValues)
      .map(value => ({
        fid: value.fid,
        value: value.value,
      })) ??
    newCriteriaValues ??
    []

  const [showCloseConfirmationDialog, setShowCloseConfirmationDialog] =
    useState(false)

  const handleCloseNewInquiry = useCallback(() => {
    if (isMessageFormEmpty) {
      closeNewInquiryDialog()
      return
    }
    setShowCloseConfirmationDialog(true)
  }, [isMessageFormEmpty, closeNewInquiryDialog])

  useEffect(() => {
    if (inquiryData) {
      closeNewInquiryDialog()
    }
  }, [inquiryData, closeNewInquiryDialog])

  return (
    <>
      {showCloseConfirmationDialog && (
        <CloseConfirmationDialog
          open={showCloseConfirmationDialog}
          isNew
          onProceed={() => {
            closeNewInquiryDialog()
            resetMessage()
          }}
          onCancel={() => {
            setShowCloseConfirmationDialog(false)
          }}
        />
      )}
      <Stack gap={0}>
        <Grid
          templateColumns={'auto 1fr auto'}
          px={5}
          py={1.5}
          justifyContent="space-between"
        >
          <HStack gap={6} minW={0} maxW="min-content" overflow="hidden">
            {(isDesktop || !!inquiryData) && (
              <HStack gap={2} minW={0} maxW="min-content" overflow="hidden">
                {isDesktop && (
                  <Tooltip content="ダイアログを移動" disabled={isDragging}>
                    <Box color="gray.600" className="drag-handle" cursor="move">
                      <DragIndicator size="lg" />
                    </Box>
                  </Tooltip>
                )}
                {!!inquiryData && (
                  <HStack gap={1} minW={0} maxW="min-content" overflow="hidden">
                    <Text
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      問い合わせID -
                    </Text>
                    <Text
                      color="gray.700"
                      fontWeight="semibold"
                      data-testid="inquiryNumber"
                    >
                      #{inquiryData.inquiryNumber}
                    </Text>
                  </HStack>
                )}
              </HStack>
            )}
            <HStack gap={1} minW={0} maxW="min-content" overflow="hidden">
              <Text
                color="gray.700"
                fontWeight="semibold"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {inquiryTitle}
              </Text>

              {isInquirer && !isCompleted && (
                <DialogRoot>
                  <Tooltip content="タイトルの編集">
                    <DialogTrigger asChild>
                      <IconButton colorPalette="gray" size="sm">
                        <EditIcon />
                      </IconButton>
                    </DialogTrigger>
                  </Tooltip>

                  <DialogContent>
                    <DialogHeader
                      fontSize="lg"
                      fontWeight="bold"
                      color="gray.700"
                    >
                      問い合わせタイトルの編集
                    </DialogHeader>
                    <DialogBody>
                      <CounterInput
                        placeholder="問い合わせタイトルを入力してください"
                        value={editingTitle}
                        onChange={e => {
                          setEditingTitle(e.target.value)
                        }}
                        invalid={editingTitle.length > 20}
                        errorText="タイトルは20文字以内で入力してください"
                        label="問い合わせタイトル"
                        maxLength={20}
                      />
                    </DialogBody>
                    <DialogFooter>
                      <DialogActionTrigger asChild>
                        <Button
                          variant="solid"
                          colorPalette="gray"
                          onClick={() => {
                            setEditingTitle(inquiryTitle)
                          }}
                        >
                          キャンセル
                        </Button>
                      </DialogActionTrigger>
                      <DialogActionTrigger asChild>
                        <Button
                          disabled={!editingTitle || editingTitle.length > 20}
                          onClick={async () => {
                            if (!inquiryData) {
                              onChangeNewInquiryTitle(editingTitle)
                              return
                            }
                            await editInquiryTitle({
                              input: {
                                inquiryUid: inquiryData.inquiryUid,
                                inquiryTitle: editingTitle,
                              },
                            })
                          }}
                        >
                          保存
                        </Button>
                      </DialogActionTrigger>
                    </DialogFooter>
                    <DialogCloseTrigger />
                  </DialogContent>
                </DialogRoot>
              )}
            </HStack>
          </HStack>

          {isDesktop && (
            // NOTE: ドラッグ操作の領域を作る
            <Box className="drag-handle" cursor="move" />
          )}

          <HStack gap="2" justify="end">
            <Tooltip content="ダイアログの最小化">
              <IconButton colorPalette="gray" onClick={onMinimize}>
                <MinimizeIcon />
              </IconButton>
            </Tooltip>

            <IconButton
              colorPalette="gray"
              onClick={() => {
                resetRejectedFiles()
                if (!inquiryData) {
                  handleCloseNewInquiry()
                  return
                }
                navigate({
                  to: '.',
                  replace: true,
                  search: {
                    inquiry: undefined,
                  },
                })
              }}
            >
              <CloseIcon />
            </IconButton>
          </HStack>
        </Grid>

        <Separator color="gray.300" />

        <Grid
          px={5}
          py={1.5}
          bgColor="white"
          rowGap="1"
          columnGap="3"
          minH="52px"
          justifyContent="space-between"
          templateColumns={{ base: '1fr', desktop: 'auto auto' }}
        >
          <HStack gap={3} minW={0} maxW="min-content" overflow="hidden">
            <Avatar size="md" name={userName} />
            <Text
              color="gray.700"
              fontWeight="semibold"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {userName}
            </Text>
            <Text
              color="gray.700"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {organizationName}
            </Text>
          </HStack>

          <HStack
            gap={2}
            justifyContent={{ base: 'space-between', desktop: 'flex-start' }}
          >
            <HStack gap={2}>
              <CriteriaValueInputDialog
                trigger={
                  <Button variant="outline" size="sm">
                    選択除外基準
                  </Button>
                }
                inquiry={inquiryData}
                trial={trialData}
                editable={
                  !isCompleted &&
                  hasPermission('InquiryEditCriteria', trialData.networkUid)
                }
                defaultCriteriaValues={defaultCriteriaValues}
                onSubmit={onChangeCriteriaValue} // 新規の場合のみ呼び出し
              />
              <AttachmentsDialog
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<DocumentIcon />}
                  >
                    問い合わせ資料
                  </Button>
                }
                inquiry={inquiryData}
              />
            </HStack>

            {hasPermission('InquiryManage', trialData.networkUid) && (
              <>
                <MenuRoot positioning={{ placement: 'bottom-end' }}>
                  <MenuTrigger asChild>
                    <IconButton colorPalette="gray">
                      <MoreVertIcon size="lg" />
                    </IconButton>
                  </MenuTrigger>
                  <Portal>
                    <MenuPositioner>
                      <MenuContent>
                        <MenuItem
                          value="complete"
                          onClick={() => {
                            if (!inquiryData || isCompleted) {
                              return
                            }
                            setCompleteDialogOpen(true)
                          }}
                          disabled={!inquiryData || isCompleted}
                        >
                          問い合わせの完了
                        </MenuItem>
                      </MenuContent>
                    </MenuPositioner>
                  </Portal>
                </MenuRoot>
                {!!inquiryData && (
                  <CompleteInquiryDialog
                    open={completeDialogOpen}
                    onOpenChange={setCompleteDialogOpen}
                    onComplete={() => {
                      completeInquiry({
                        input: {
                          inquiryUid: inquiryData.inquiryUid,
                        },
                      })
                    }}
                  />
                )}
              </>
            )}
          </HStack>
        </Grid>
      </Stack>
    </>
  )
}

const inquiryMessageContentFragment = graphql(`
  fragment InquiryMessageContent on InquiryMessage {
    inquiryMessageUid
    message
    isEdited
    postedBy
    postedAt
    updatedAt
    attachments {
      inquiryMessageAttachmentUid
      deletedAt
      uploadedFile {
        uploadedFileUid
        name
        size
        url(mode: Attachment)
      }
    }
    postedUser {
      ... on BusinessUser {
        organizationName
        generalUser {
          firstName
          lastName
        }
      }
    }
  }
`)
type InquiryMessageProps = {
  message: FragmentType<typeof inquiryMessageContentFragment>
  isCompleted: boolean
  mutateInquiry: () => void
}

const editInquiryMessageMutation = graphql(`
  mutation EditInquiryMessage($input: EditInquiryMessageInput!) {
    editInquiryMessage(input: $input) {
      inquiryMessageUid
    }
  }
`)
const deleteInquiryMessageMutation = graphql(`
  mutation DeleteInquiryMessage($input: DeleteInquiryMessageInput!) {
    deleteInquiryMessage(input: $input) {
      inquiryMessageUid
    }
  }
`)

const InquiryMessage = ({
  message,
  mutateInquiry,
  isCompleted,
}: InquiryMessageProps) => {
  const user = useAuthenticatedUser()
  const messageData = getFragmentData(inquiryMessageContentFragment, message)

  const [isEditing, setIsEditing] = useState(false)

  const isMyMessage = messageData.postedBy === user.userUid
  const bgColor = isMyMessage ? 'white' : 'gray.50'

  const defaultEditingItem = {
    message: messageData.message,
  }

  const [editingItem, setEditingItem] = useState(defaultEditingItem)

  // ファイルの削除などが合った際にstateが更新する
  useEffect(() => {
    setEditingItem(current => ({
      ...current,
      files: messageData.attachments.map(a => ({
        uploadedFileUid: a.uploadedFile.uploadedFileUid,
        fileName: a.uploadedFile.name,
        size: a.uploadedFile.size,
      })),
    }))
  }, [messageData.attachments])

  const reset = () => {
    setEditingItem(defaultEditingItem)
  }

  const isEmpty = !editingItem.message

  const request = async () => {
    await graphqlRequest(editInquiryMessageMutation, {
      input: {
        inquiryMessageUid: messageData.inquiryMessageUid,
        message: editingItem.message,
      },
    })
  }

  const { request: submitEditMessage, isPending } = useMutation(request, {
    onSuccess: () => {
      setIsEditing(false)
      mutateInquiry()
    },
  })

  const { request: deleteInquiryMessage } = useGqlMutation(
    deleteInquiryMessageMutation,
    {
      onSuccess: () => {
        toaster.create({
          type: 'success',
          title: 'メッセージを削除しました',
        })
        mutateInquiry()
      },
    },
  )

  return (
    <Stack
      p={3}
      gap={2}
      borderRadius={2}
      bgColor={bgColor}
      ml={isMyMessage ? '8' : '0'}
      mr={isMyMessage ? '0' : '8'}
    >
      <HStack gap={2} justifyContent="space-between">
        <Box>
          <HStack gap={2} alignItems="baseline">
            <Avatar
              size="sm"
              name={messageData.postedUser.generalUser.lastName}
            />
            <Text color="text.sub" fontSize="sm" fontWeight="semibold">
              {getFullName({
                firstName: messageData.postedUser.generalUser.firstName,
                lastName: messageData.postedUser.generalUser.lastName,
              })}
            </Text>
            <Text color="text.sub" fontSize="xs">
              {messageData.postedUser.organizationName}
            </Text>
          </HStack>
          <Text color="text.sub" fontSize="xs">
            {formatDate(messageData.postedAt, 'YYYY/MM/DD HH:mm')}
            {messageData.isEdited &&
              ` (編集済: ${formatDate(messageData.updatedAt, 'YYYY/MM/DD HH:mm')})`}
          </Text>
        </Box>

        {isMyMessage && !isCompleted && (
          <HStack gap={1}>
            <DialogRoot>
              <Tooltip content="メッセージの削除">
                <DialogTrigger asChild>
                  <IconButton colorPalette="red" size="sm">
                    <DeleteIcon />
                  </IconButton>
                </DialogTrigger>
              </Tooltip>

              <DialogContent>
                <DialogHeader fontSize="lg" fontWeight="bold" color="gray.700">
                  メッセージを削除する
                </DialogHeader>
                <DialogBody>
                  <Text>
                    このメッセージを削除しますか？削除後は元に戻すことはできません。
                  </Text>
                </DialogBody>
                <DialogFooter>
                  <DialogActionTrigger asChild>
                    <Button variant="solid" colorPalette="gray">
                      キャンセル
                    </Button>
                  </DialogActionTrigger>
                  <Button
                    colorPalette="red"
                    onClick={async () => {
                      await deleteInquiryMessage({
                        input: {
                          inquiryMessageUid: messageData.inquiryMessageUid,
                        },
                      })
                    }}
                  >
                    削除
                  </Button>
                </DialogFooter>
                <DialogCloseTrigger />
              </DialogContent>
            </DialogRoot>

            <Tooltip content="メッセージの編集">
              <IconButton
                size="sm"
                onClick={() => {
                  setIsEditing(true)
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </HStack>
        )}
      </HStack>

      {isEditing ? (
        <MessageEditForm
          requesting={isPending}
          message={editingItem.message}
          isEmpty={isEmpty}
          onChangeMessage={message => {
            setEditingItem(current => ({
              ...current,
              message,
            }))
          }}
          onSubmit={submitEditMessage}
          onCancel={() => {
            setIsEditing(false)
            reset()
          }}
        />
      ) : (
        <Stack gap="3" w="full" overflow="auto">
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {messageData.message}
          </Text>

          {/* FIXME: 添付ファイルの数だけ表示させるように <For /> でループ、onClickでダウンロードされるように */}
          {messageData.attachments.length > 0 && (
            <HStack gap="2" w="full" wrap="wrap">
              {messageData.attachments.map(attachment => {
                return attachment.deletedAt ? (
                  <Tag
                    key={attachment.inquiryMessageAttachmentUid}
                    colorPalette="gray"
                    variant="subtle"
                    size="sm"
                    opacity={0.4}
                    startElement={<DocumentIcon />}
                  >
                    このファイルは削除されました
                  </Tag>
                ) : (
                  <Tag
                    key={attachment.inquiryMessageAttachmentUid}
                    colorPalette="gray"
                    variant="subtle"
                    size="sm"
                    startElement={<DocumentIcon />}
                    wordBreak="break-all"
                    cursor="pointer"
                    // 編集中の遷移アラート対策で別タブで開く（一瞬チラつくが許容）
                    onClick={() => {
                      window.open(attachment.uploadedFile.url, '_blank')
                      toaster.create({
                        type: 'success',
                        description: '1件のファイルをダウンロードしました',
                      })
                    }}
                  >
                    {attachment.uploadedFile.name}
                  </Tag>
                )
              })}
            </HStack>
          )}
        </Stack>
      )}
    </Stack>
  )
}

const NotificationMessage = ({
  message,
  savedAt,
}: {
  message: string
  savedAt: string
}) => {
  return (
    <Center p="10px">
      <Text
        color="text.sub"
        fontSize="xs"
        whiteSpace="pre-wrap"
        textAlign="center"
      >
        {`${message}\n${formatDate(savedAt, 'YYYY/MM/DD HH:mm')}`}
      </Text>
    </Center>
  )
}

const inquiryNotificationInquiryLogFragment = graphql(`
  fragment InquiryNotificationInquiryLog on InquiryLog {
    action
    author {
      ... on BusinessUser {
        generalUser {
          firstName
          lastName
        }
      }
    }
    savedAt
  }
`)
type InquiryNotificationProps = {
  log: FragmentType<typeof inquiryNotificationInquiryLogFragment>
}
const InquiryNotification = ({ log }: InquiryNotificationProps) => {
  const logData = getFragmentData(inquiryNotificationInquiryLogFragment, log)
  if (
    logData.action === InquiryLogAction.Create ||
    logData.action === InquiryLogAction.EditTitle
  ) {
    return null
  }

  const userName = getFullName({
    firstName: logData.author.generalUser.firstName,
    lastName: logData.author.generalUser.lastName,
  })
  const message = (() => {
    switch (logData.action) {
      case InquiryLogAction.Complete:
        return `${userName}さんが問い合わせを完了しました`
      case InquiryLogAction.Delete:
        return `${userName}さんが問い合わせをキャンセルしました`
      case InquiryLogAction.EditCriteria:
        return `${userName}さんが選択除外基準を更新しました`
      case InquiryLogAction.Reopen:
        return `${userName}さんが問い合わせを再開しました`
      default:
        return ''
    }
  })()

  return <NotificationMessage message={message} savedAt={logData.savedAt} />
}
const inquiryDialogContentInquiryFragment = graphql(`
  fragment InquiryDialogContentInquiry on Inquiry {
    latestLog {
      status
    }
    messages(order: PostedAtAsc, includeDeleted: true) {
      ...InquiryMessageContent
      __typename
      inquiryMessageUid
      replacedInquiryMessageUid
      postedAt # sortで使用しているので未使用warningは無視
      deletedAt
      postedUser {
        ... on BusinessUser {
          generalUser {
            firstName
            lastName
          }
        }
      }
    }
    logs {
      ...InquiryNotificationInquiryLog
      __typename
      inquiryLogUid
      savedAt # sortで使用しているので未使用warningは無視
    }
  }
`)
type InquiryDialogContentsProps = {
  inquiry?: FragmentType<typeof inquiryDialogContentInquiryFragment>
  mutateInquiry: () => void
}

const InquiryDialogContents = ({
  inquiry,
  mutateInquiry,
}: InquiryDialogContentsProps) => {
  const scrollBottomRef = useRef<HTMLDivElement>(null)

  const inquiryData = getFragmentData(
    inquiryDialogContentInquiryFragment,
    inquiry,
  )

  // messageとlogを時系列に並び替える（表示順に並び替える）
  const messagesAndLogs = [
    ...(inquiryData?.messages ?? []),
    ...(inquiryData?.logs ?? []),
  ].toSorted((a, b) => {
    const aTime = a.__typename === 'InquiryMessage' ? a.postedAt : a.savedAt
    const bTime = b.__typename === 'InquiryMessage' ? b.postedAt : b.savedAt
    return dayjs(aTime).isBefore(dayjs(bTime)) ? -1 : 1
  })

  // 常に最新のメッセージが表示されるようにする
  useEffect(() => {
    if (!scrollBottomRef.current) return
    scrollBottomRef.current.scrollIntoView({ behavior: 'auto' })
  }, [messagesAndLogs.length])

  return (
    <Stack p={2} gap={4} bgColor="blue.50" overflow="scroll" flex="1">
      <For each={messagesAndLogs}>
        {item => {
          switch (item.__typename) {
            case 'InquiryMessage':
              if (item.deletedAt) {
                const message = `${getFullName(item.postedUser.generalUser)}さんがメッセージの送信を取り消しました`
                return (
                  <NotificationMessage
                    message={message}
                    savedAt={item.deletedAt}
                  />
                )
              }
              return (
                <InquiryMessage
                  key={item.inquiryMessageUid}
                  message={item}
                  mutateInquiry={mutateInquiry}
                  isCompleted={
                    inquiryData?.latestLog.status === InquiryLogStatus.Completed
                  }
                />
              )
            case 'InquiryLog':
              return <InquiryNotification key={item.inquiryLogUid} log={item} />
            default:
              assertNever(item)
          }
        }}
      </For>
      <div ref={scrollBottomRef} />
    </Stack>
  )
}

type MessageFormProps = {
  requesting: boolean
  message: string
  fileUpload: UseFileUploadReturn
  isEmpty: boolean
  editingSavedFiles?: {
    uploadedFileUid: string
    size: number
    fileName: string
  }[]
  otherMessagesTotalFileCount: number
  otherMessagesTotalFileSize: number
  onChangeMessage: (message: string) => void
  onSubmit: () => Promise<void>
  onDeleteSavedFile?: (uploadedFileUid: string) => void
  onCancel?: () => void
}
const MessageForm = ({
  requesting,
  message,
  fileUpload,
  isEmpty,
  otherMessagesTotalFileCount,
  otherMessagesTotalFileSize,
  editingSavedFiles = [],
  onChangeMessage,
  onSubmit,
  onDeleteSavedFile,
  onCancel,
}: MessageFormProps) => {
  const acceptedFiles = fileUpload.acceptedFiles

  const hasInvalidExtensionFile = fileUpload.rejectedFiles.length > 0

  const handleSubmit = async () => {
    if (isEmpty) return
    await onSubmit()
  }

  const savedFileTotalSize = editingSavedFiles.reduce(
    (acc, file) => acc + file.size,
    0,
  )
  const newlyAddedFileTotalSize = acceptedFiles.reduce(
    (acc, file) => acc + file.size,
    0,
  )

  const singleMessageFileCount = editingSavedFiles.length + acceptedFiles.length

  const totalFileSize =
    otherMessagesTotalFileSize + savedFileTotalSize + newlyAddedFileTotalSize

  const totalFileCount =
    otherMessagesTotalFileCount +
    editingSavedFiles.length +
    acceptedFiles.length

  const errorMessages = validateInquiryMessage(
    message,
    acceptedFiles,
    singleMessageFileCount,
    totalFileCount,
    totalFileSize,
  )

  const isInvalid = errorMessages.length > 0

  return (
    <FileUploadRootProvider value={fileUpload} w="full">
      <Box w="full">
        <FieldRoot>
          {(isInvalid || hasInvalidExtensionFile) && (
            <Stack>
              {errorMessages.map((errorMessage, i) => (
                <Text
                  key={`${errorMessage}_${i}`}
                  color="text.error"
                  fontSize="xs"
                  whiteSpace="pre-wrap"
                >
                  {errorMessage}
                </Text>
              ))}
              {hasInvalidExtensionFile && (
                <Text color="text.error" fontSize="xs" whiteSpace="pre-wrap">
                  {`アップロードできないフォーマットのファイルを除外しました。\n※アップロード可能なファイル形式（${ACCEPTABLE_EXTENSIONS.map(extension => extension).join('・')}）`}
                </Text>
              )}
            </Stack>
          )}
          <Box
            w="full"
            bg="white"
            borderWidth="1px"
            borderTopRadius="md"
            borderColor={isInvalid ? 'border.error' : undefined}
            css={{
              '&:has(> textarea:focus-visible)': {
                borderWidth: '1px',
                outline: 'none',
                borderColor: isInvalid ? 'border.error' : 'blue.400',
                caretColor: 'status.primary',
              },
            }}
          >
            <Textarea
              value={message}
              placeholder="メッセージの入力"
              onChange={e => onChangeMessage(e.target.value)}
              autoresize
              minH="100px"
              maxH="200px"
              resize="none"
              borderRadius="md"
              border="none"
              bgColor="white"
              _focusVisible={{
                border: 'none',
                outline: 'none',
              }}
            />
            {(editingSavedFiles?.length ?? 0) + acceptedFiles.length > 0 && (
              <Box bg="white" w="full" px="3" py="2">
                <HStack gap="2" w="full" wrap="wrap">
                  {editingSavedFiles?.map(file => (
                    <Tag
                      key={file.uploadedFileUid}
                      variant="subtle"
                      colorPalette="gray"
                      size="sm"
                      startElement={<DocumentIcon />}
                      endElement={
                        <Box
                          as="button"
                          onClick={() => {
                            onDeleteSavedFile?.(file.uploadedFileUid)
                          }}
                        >
                          <CloseIcon />
                        </Box>
                      }
                    >
                      {file.fileName}
                    </Tag>
                  ))}
                  {acceptedFiles.map(file => (
                    <Tag
                      key={`${file.name}-${file.lastModified}`}
                      variant="subtle"
                      colorPalette="gray"
                      wordBreak="break-all"
                      size="sm"
                      startElement={<DocumentIcon />}
                      endElement={
                        <Box
                          as="button"
                          onClick={() => fileUpload.deleteFile(file)}
                        >
                          <CloseIcon />
                        </Box>
                      }
                    >
                      {file.name}
                    </Tag>
                  ))}
                </HStack>
              </Box>
            )}
          </Box>
        </FieldRoot>
        <HStack
          px="3"
          py="1"
          gap="2"
          justifyContent="space-between"
          bgColor="white"
          borderBottomRadius="md"
          borderWidth="1px"
          borderTopWidth="0px"
        >
          <FileUploadHiddenInput />
          <Tooltip content="ファイルを添付">
            <FileUploadTrigger asChild>
              <IconButton name="upload-file">
                <AttachFileIcon />
              </IconButton>
            </FileUploadTrigger>
          </Tooltip>
          <HStack>
            {!!onCancel && (
              <Button size="sm" variant="ghost" onClick={onCancel}>
                キャンセル
              </Button>
            )}
            <Button
              size="sm"
              loading={requesting}
              onClick={handleSubmit}
              disabled={isEmpty || isInvalid}
            >
              送信
            </Button>
          </HStack>
        </HStack>
      </Box>
    </FileUploadRootProvider>
  )
}

type MessageEditFormProps = {
  requesting: boolean
  message: string
  isEmpty: boolean
  onChangeMessage: (message: string) => void
  onSubmit: () => Promise<void>
  onCancel?: () => void
}
const MessageEditForm = ({
  requesting,
  message,
  isEmpty,
  onChangeMessage,
  onSubmit,
  onCancel,
}: MessageEditFormProps) => {
  const handleSubmit = async () => {
    if (isEmpty) return
    await onSubmit()
  }

  const errorMessage = validateMessageLength(message)

  const isInvalid = errorMessage !== null

  return (
    <Box w="full">
      <FieldRoot gap="0.5">
        {isInvalid && (
          <Text color="text.error" fontSize="xs" whiteSpace="pre-wrap">
            {errorMessage}
          </Text>
        )}
        <Textarea
          value={message}
          onChange={e => onChangeMessage(e.target.value)}
          pos="relative"
          minH="100px"
          autoresize
          resize="none"
          borderTopRadius="md"
          borderWidth="1px"
          bgColor="white"
          borderColor={isInvalid ? 'border.error' : undefined}
          placeholder="メッセージの入力"
          _focusVisible={{
            caretColor: 'status.primary',
            borderColor: 'blue.400',
            outline: 'none',
          }}
        />
      </FieldRoot>
      <HStack
        px="3"
        py="1"
        gap="2"
        justifyContent="flex-end"
        bgColor="white"
        borderBottomRadius="md"
        borderWidth="1px"
        borderTopWidth="0px"
      >
        <HStack>
          {!!onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel}>
              キャンセル
            </Button>
          )}
          <Button
            size="sm"
            loading={requesting}
            onClick={handleSubmit}
            disabled={isEmpty || isInvalid}
          >
            送信
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
}

const minimizedDialogInquiryFragment = graphql(`
  fragment MinimizedInquiryDialogInquiry on Inquiry {
    inquiryNumber
  }
`)
const MinimizedInquiryDialog = ({
  onMaximize,
  isDesktop,
  inquiry,
}: {
  onMaximize: IconButtonProps['onClick']
  isDesktop: boolean
  inquiry?: FragmentType<typeof minimizedDialogInquiryFragment>
}) => {
  const inquiryData = getFragmentData(minimizedDialogInquiryFragment, inquiry)
  const height = 60
  const width = 360
  const padding = 8

  const { position, onChangePosition } = useInquiryDialogPosition({
    defaultPosition: {
      x: window.innerWidth - (width + padding),
      y: window.innerHeight - (height + padding) - 64,
    },
    minWidth: width,
    minHeight: height,
    bottomOffset: 64,
  })

  const { title: newInquiryTitle } = useNewInquiryContext()

  return (
    <Rnd
      default={{
        x: position.x,
        y: position.y,
        width: isDesktop ? 'fit-content' : '100svw',
        height: 'fit-content',
      }}
      position={position}
      onDragStop={(_, data) => {
        onChangePosition({ x: data.x, y: data.y })
      }}
      enableResizing={false}
      bounds="window"
      disableDragging={!isDesktop}
    >
      <HStack
        w={`${width}px`}
        h={`${height}px`}
        px={4}
        py={2}
        gap={2}
        justifyContent="space-between"
        bgColor="blue.100"
        boxShadow="lg"
      >
        {inquiryData ? (
          <HStack gap={1}>
            <Text color="gray.700" fontSize="sm" fontWeight="semibold">
              問い合わせID -
            </Text>
            <Text color="gray.700" fontWeight="semibold">
              #{inquiryData.inquiryNumber}
            </Text>
          </HStack>
        ) : (
          <Text color="gray.700" fontWeight="semibold">
            {newInquiryTitle}
          </Text>
        )}
        <IconButton colorPalette="gray" onClick={onMaximize}>
          <WebAssetIcon />
        </IconButton>
      </HStack>
    </Rnd>
  )
}

const PostMessageConfirmationDialog = ({
  open,
  requesting,
  onClose,
  onSubmit,
  userUid,
}: {
  open: boolean
  requesting: boolean
  onClose: () => void
  onSubmit: () => Promise<void>
  userUid: string
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const handleClose = () => {
    onClose()
  }

  const handleSubmit = async () => {
    if (dontShowAgain) {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEYS.getUserPostMessageConfirmationKey(userUid),
        'false',
      )
    }

    await onSubmit()
    onClose()
  }

  return (
    <DialogRoot open={open}>
      <DialogContent>
        <DialogHeader fontSize="lg" fontWeight="bold" color="gray.700">
          問い合わせメッセージを送信しますか？
        </DialogHeader>
        <DialogBody>
          <Checkbox
            onCheckedChange={e => {
              setDontShowAgain(!!e.checked)
            }}
          >
            今後このメッセージを表示しない
          </Checkbox>
        </DialogBody>
        <DialogFooter>
          <Button variant="solid" colorPalette="gray" onClick={handleClose}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            colorPalette="blue"
            loading={requesting}
          >
            送信
          </Button>
        </DialogFooter>
        <DialogCloseTrigger onClick={handleClose} />
      </DialogContent>
    </DialogRoot>
  )
}

const inquiryDialogContentFragment = graphql(`
  fragment InquiryDialogContent on Inquiry {
    messages(order: PostedAtAsc, includeDeleted: true) {
      attachments {
        deletedAt
        uploadedFile {
          size
        }
      }
    }
    ...InquiryHeaderInquiry
    ...InquiryDialogContentInquiry
  }
`)
const inquiryDialogContentTrialFragment = graphql(`
  fragment InquiryDialogContentTrial on Trial {
    ...InquiryHeaderTrial
  }
`)
type ContentProps = {
  inquiry: FragmentType<typeof inquiryDialogContentFragment> | undefined
  trial: FragmentType<typeof inquiryDialogContentTrialFragment>
  isDragging: boolean
  isEmpty: boolean
  message: string
  fileUpload: UseFileUploadReturn
  criteriaValues: CriteriaValue[]
  isMessagePosting: boolean
  mutateInquiry: () => void
  onMinimize: () => void
  setMessage: (message: string) => void
  setCriteriaValues: (values: CriteriaValue[]) => void
  onSubmitMessage: () => Promise<void>
  resetMessage: () => void
}

const InquiryDialogContent = ({
  inquiry,
  trial,
  isDragging,
  mutateInquiry,
  onMinimize,
  message,
  setMessage,
  fileUpload,
  criteriaValues,
  isMessagePosting,
  isEmpty,
  setCriteriaValues,
  onSubmitMessage,
  resetMessage,
}: ContentProps) => {
  const inquiryData = getFragmentData(inquiryDialogContentFragment, inquiry)
  const trialData = getFragmentData(inquiryDialogContentTrialFragment, trial)

  const user = useAuthenticatedUser()

  const userSpecificSetting = window.localStorage.getItem(
    LOCAL_STORAGE_KEYS.getUserPostMessageConfirmationKey(user.userUid),
  )

  const postMessageConfirmationRequired = userSpecificSetting !== 'false'
  const [postMessageConfirmationOpen, setPostMessageConfirmationOpen] =
    useState(false)

  const attachments =
    inquiryData?.messages
      .flatMap(m => m.attachments)
      .filter(a => {
        return a.deletedAt === null
      }) ?? []
  const totalFileCount = attachments.length
  const totalFileSize = attachments.reduce(
    (acc, attachment) => acc + attachment.uploadedFile.size,
    0,
  )

  return (
    <Grid
      h="full"
      templateRows={'auto 1fr auto'}
      border="1px solid"
      borderColor="border"
      gap={0}
      p={0}
      bgColor="blue.100"
      borderRadius="base"
      boxShadow="lg"
    >
      <InquiryDialogHeader
        trial={trialData}
        inquiry={inquiryData}
        isDragging={isDragging}
        isMessageFormEmpty={isEmpty}
        onMinimize={onMinimize}
        newCriteriaValues={criteriaValues}
        onChangeCriteriaValue={
          inquiryData
            ? null
            : values => {
                setCriteriaValues(values)
              }
        }
        resetMessage={resetMessage}
        resetRejectedFiles={fileUpload.clearRejectedFiles}
      />

      <InquiryDialogContents
        inquiry={inquiryData}
        mutateInquiry={mutateInquiry}
      />

      <Box p={4} h="fit-content">
        <MessageForm
          message={message}
          onChangeMessage={setMessage}
          fileUpload={fileUpload}
          isEmpty={isEmpty}
          onSubmit={async () => {
            if (postMessageConfirmationRequired) {
              setPostMessageConfirmationOpen(true)
            } else {
              await onSubmitMessage()
            }
          }}
          requesting={isMessagePosting}
          otherMessagesTotalFileCount={totalFileCount}
          otherMessagesTotalFileSize={totalFileSize}
        />
      </Box>
      <PostMessageConfirmationDialog
        open={postMessageConfirmationOpen}
        requesting={isMessagePosting}
        onClose={() => {
          setPostMessageConfirmationOpen(false)
        }}
        onSubmit={onSubmitMessage}
        userUid={user.userUid}
      />
    </Grid>
  )
}

const DIALOG_MIN_WIDTH = 400
const DIALOG_MIN_HEIGHT = 600

type Props = {
  inquiryUid?: string
  trial: FragmentType<typeof inquiryDialogTrialFragment>
  isOpen: boolean
  defaultLayout: {
    position: { x: number; y: number }
    size: { width: number; height: number }
  }
}
const getInquiryQuery = graphql(`
  query GetInquiry($inquiryUid: UUID!) {
    inquiry(inquiryUid: $inquiryUid) {
      inquiryUid
      messages(order: PostedAtAsc, includeDeleted: true) {
        inquiryMessageUid
        postedBy
      }
      ...MinimizedInquiryDialogInquiry
      ...InquiryDialogContent
    }
  }
`)
const browseInquiryMutation = graphql(`
  mutation BrowseInquiry($input: BrowseInquiryMessageInput!) {
    browseInquiryMessage(input: $input) {
      inquiryMessageUid
    }
  }
`)
const browseInquiryMessages = async (messageUids: string[]) => {
  await Promise.all(
    messageUids.map(messageUid =>
      graphqlRequest(browseInquiryMutation, {
        input: {
          inquiryMessageUid: messageUid,
        },
      }),
    ),
  )
}
const postInquiryMutation = graphql(`
  mutation PostInquiry($input: PostInquiryInput!) {
    postInquiry(input: $input) {
      inquiryUid
    }
  }
`)
const postInquiryMessageMutation = graphql(`
  mutation PostInquiryMessage($input: PostInquiryMessageInput!) {
    postInquiryMessage(input: $input) {
      __typename
    }
  }
`)

const inquiryDialogTrialFragment = graphql(`
  fragment InquiryDialogTrial on Trial {
    trialNumber
    trialUid
    networkUid
    trialUid
    latestCriteria {
      trialCriteriaUid
    }
    ...InquiryDialogContentTrial
  }
`)
export const InquiryDialog = ({
  inquiryUid,
  trial,
  isOpen,
  defaultLayout,
}: Props) => {
  const navigate = useNavigate()
  const alreadyReadMessageUids = useRef<string[]>([])
  const trialData = getFragmentData(inquiryDialogTrialFragment, trial)

  const user = useAuthenticatedUser()

  const { data: inquiryData, mutate: mutateInquiry } = useGqlQuery(
    {
      document: getInquiryQuery,
      variable: inquiryUid
        ? {
            inquiryUid,
          }
        : null,
    },
    {
      refreshInterval: 5000,
      onSuccess: async ({ inquiry }) => {
        // 閲覧ログの記録
        const newlyReadMessageUids = inquiry.messages
          .filter(
            m =>
              !alreadyReadMessageUids.current.includes(m.inquiryMessageUid) &&
              m.postedBy !== user.userUid,
          )
          .map(m => m.inquiryMessageUid)
        alreadyReadMessageUids.current.push(...newlyReadMessageUids)
        if (newlyReadMessageUids.length > 0) {
          await browseInquiryMessages(newlyReadMessageUids)
          mutateCache(
            generateGraphQLCacheKey(GetInquiriesDocument, {
              trialUid: trialData.trialUid,
            }),
          )
        }
      },
      onError: error => {
        // 認証エラーの場合は再throwしてrootのerrorComponentでハンドリングする
        // ログイン画面へのリダイレクトとtoastの表示が重なってしまうのを防ぐため
        if (error.status === 401) {
          throw error
        }
        // UUIDのフォーマット違いなどが400になるので400の場合も同じエラーを表示
        if (error.status === 404 || error.status === 400) {
          toaster.create({
            title: '問い合わせが見つかりませんでした',
            type: 'error',
          })
        } else {
          toaster.create({
            type: 'error',
            title: error.message,
          })
        }
        // 問い合わせを表示しない治験詳細画面に戻す
        navigate({
          to: '/trials/$trialNumber',
          replace: true,
          params: { trialNumber: trialData.trialNumber },
          search: {
            inquiry: undefined,
          },
        })
      },
    },
  )

  // Dialogのresizeなどをした場合に値を保持するようルートでstateを持つ
  const [message, setMessage] = useState('')
  const fileUpload = useFileUpload({
    maxFiles: 100, // 上限は15だが、15以上アップロードした際は自前のバリデーションを適用したいので十分に大きい値を設定
    // 拡張子のバリデーションだけは正しくない場合に除外するという要件があるのでここでvalidateする
    validate: file => {
      if (validateFileExtension(file)) {
        return null
      }
      return ['FILE_INVALID']
    },
  })
  // 新規の場合のcriteriaValue
  const [criteriaValues, setCriteriaValues] = useState<CriteriaValue[]>([])

  const isNewCriteriaValueEditing = criteriaValues.length > 0 && !inquiryData
  const isEmpty =
    !message &&
    fileUpload.acceptedFiles.length === 0 &&
    !isNewCriteriaValueEditing

  const [isMinimized, setIsMinimized] = useState(false)
  const isDesktop = useBreakpointValue({ base: false, desktop: true }) ?? false

  const [isDragging, setIsDragging] = useState(false)

  const defaultPosition = useMemo(() => {
    return {
      x: defaultLayout.position.x,
      y: defaultLayout.position.y,
      width: defaultLayout.size.width,
      height: defaultLayout.size.height,
    }
  }, [defaultLayout])
  const { position, onChangePosition } = useInquiryDialogPosition({
    defaultPosition,
    minWidth: Math.min(DIALOG_MIN_WIDTH, window.innerWidth - 16),
    minHeight: Math.min(DIALOG_MIN_HEIGHT, window.innerHeight - 16),
  })

  useEffect(() => {
    // 閉じたら次はデフォルトの位置で開く
    if (!isOpen) {
      onChangePosition(defaultPosition)
    }
  }, [isOpen, onChangePosition, defaultPosition])

  const postInquirySucceeded = useRef(false)
  // see: https://tanstack.com/router/v1/docs/framework/react/guide/navigation-blocking
  const { status, proceed, reset } = useBlocker({
    shouldBlockFn: () => {
      return !isEmpty && !postInquirySucceeded.current
    },
    disabled: !isOpen,
    withResolver: true,
  })

  const resetMessage = useCallback(() => {
    setMessage('')
    fileUpload.clearFiles()
    setCriteriaValues([])
  }, [fileUpload, setMessage])

  const { title: newInquiryTitle, closeInquiryDialog } = useNewInquiryContext()

  const { request: postInquiry, isPending: isPostInquiryPending } =
    useGqlMutation(postInquiryMutation, {
      onSuccess: ({ postInquiry: { inquiryUid } }) => {
        postInquirySucceeded.current = true
        resetMessage()
        closeInquiryDialog()
        navigate({
          to: '.',
          replace: true,
          search: {
            inquiry: inquiryUid,
          },
        })
        // そのまま次のメッセージの編集状態になったらアラートが出るように値を戻しておく
        postInquirySucceeded.current = false
      },
    })
  const {
    request: postInquiryMessage,
    isPending: isPendingPostInquiryMessage,
  } = useGqlMutation(postInquiryMessageMutation, {
    onSuccess: () => {
      resetMessage()
      mutateInquiry()
    },
  })

  const handleSubmitMessage = useCallback(async () => {
    const files = fileUpload.acceptedFiles
    let attachments: NewInquiryMessageInputAttachment[] = []
    if (files.length > 0) {
      try {
        const { uploadedFileUids } = await uploadFiles(
          trialData.networkUid,
          files,
        )
        attachments = uploadedFileUids.map((uploadedFileUid, i) => ({
          uploadedFileUid,
          attachmentOrder: i + 1,
        }))
      } catch (error) {
        let message: string = 'ファイルのアップロードに失敗しました'
        if (error instanceof ApiError) {
          message = error.message
        }
        toaster.create({
          type: 'error',
          title: message,
        })
        return
      }
    }
    // 初回は問い合わせとメッセージを同時に作成
    if (!inquiryData) {
      await postInquiry({
        input: {
          inquiryTitle: newInquiryTitle,
          message,
          attachments,
          criteria: [
            {
              trialCriteriaUid: trialData.latestCriteria[0].trialCriteriaUid,
              values: criteriaValues.map(v => ({
                fid: v.fid,
                value: v.value,
                fieldIndex: 1,
              })),
            },
          ],
          trialUid: trialData.trialUid,
          networkUid: trialData.networkUid,
        },
      })
      return
    }

    await postInquiryMessage({
      input: {
        inquiryUid: inquiryData.inquiry.inquiryUid,
        message,
        attachments,
      },
    })
  }, [
    inquiryData,
    message,
    fileUpload,
    postInquiry,
    criteriaValues,
    postInquiryMessage,
    trialData,
    newInquiryTitle,
  ])

  const contentProps: ContentProps = useMemo(
    () => ({
      inquiry: inquiryData?.inquiry,
      trial: trialData,
      isEmpty,
      isDragging,
      mutateInquiry,
      message,
      setMessage,
      fileUpload,
      criteriaValues,
      setCriteriaValues,
      onMinimize: () => setIsMinimized(true),
      onSubmitMessage: handleSubmitMessage,
      resetMessage,
      isMessagePosting: isPostInquiryPending || isPendingPostInquiryMessage,
    }),
    [
      inquiryData,
      trialData,
      isEmpty,
      isDragging,
      mutateInquiry,
      message,
      setMessage,
      fileUpload,
      criteriaValues,
      setCriteriaValues,
      handleSubmitMessage,
      resetMessage,
      isPostInquiryPending,
      isPendingPostInquiryMessage,
    ],
  )

  if (!isOpen) {
    return null
  }
  if (!!inquiryUid && !inquiryData && !postInquirySucceeded.current) {
    return null
  }

  return (
    <>
      <CloseConfirmationDialog
        open={status === 'blocked'}
        isNew={!inquiryData}
        onCancel={() => {
          reset?.()
        }}
        onProceed={() => {
          proceed?.()
          resetMessage()
          // 次の問い合わせは開いた状態にする
          setIsMinimized(false)
        }}
      />
      {isMinimized && (
        <MinimizedInquiryDialog
          isDesktop={isDesktop}
          onMaximize={() => setIsMinimized(false)}
          inquiry={inquiryData?.inquiry}
        />
      )}
      {!isMinimized &&
        (isDesktop ? (
          <Rnd
            default={{
              x: position.x,
              y: position.y,
              width: position.width ?? DIALOG_MIN_WIDTH,
              height: position.height ?? DIALOG_MIN_HEIGHT,
            }}
            position={{ x: position.x, y: position.y }}
            size={{
              width: position.width ?? DIALOG_MIN_WIDTH,
              height: position.height ?? DIALOG_MIN_HEIGHT,
            }}
            minWidth={Math.min(DIALOG_MIN_WIDTH, window.innerWidth - 16)}
            minHeight={Math.min(DIALOG_MIN_HEIGHT, window.innerHeight - 16)}
            maxWidth={window.innerWidth - 16}
            maxHeight={window.innerHeight - 16}
            dragHandleClassName="drag-handle"
            bounds="window"
            onDragStart={() => setIsDragging(true)}
            onDragStop={(_, draggableData) => {
              setIsDragging(false)
              onChangePosition({
                x: draggableData.x,
                y: draggableData.y,
              })
            }}
            onResize={(_e, _direction, ref, _delta, position) => {
              onChangePosition({
                x: position.x,
                y: position.y,
                width: ref.offsetWidth,
                height: ref.offsetHeight,
              })
            }}
          >
            <InquiryDialogContent {...contentProps} />
          </Rnd>
        ) : (
          // spではDragできない全画面表示
          <Box
            h="100svh"
            w="100svw"
            bgColor="blue.100"
            pos="fixed"
            top="0"
            left="0"
          >
            <InquiryDialogContent {...contentProps} />
          </Box>
        ))}
    </>
  )
}
